/* Proveedores › Facturas a pagar: lo que se dice de cada factura, sin tocar la
 * base.
 *
 * Vive acá y no en la pantalla para poder probarlo solo: qué botones ve cada
 * uno, qué dice cada aviso, qué cuenta el resumen. Las REGLAS (quién puede
 * qué, qué traba, qué hay que confirmar) son de la base; esto sólo decide
 * cómo se muestran y no deja apretar lo que la base va a rechazar.
 */

import { normalizar as sinTildes } from "./buscar";
import { cuandoLlego, TZ } from "./facturasRemitos";

// ── Estados ─────────────────────────────────────────────────────────────────

export const ESTADOS = {
  sin_mirar: { rotulo: "Sin mirar", clase: "gris" },
  para_autorizar: { rotulo: "Esperando autorización", clase: "info" },
  a_pagar: { rotulo: "A pagar", clase: "aviso" },
  vino_paga: { rotulo: "Vino paga", clase: "info" },
  pagada: { rotulo: "Pagada", clase: "ok" },
  rechazada: { rotulo: "Rechazada", clase: "mal" },
  no_va: { rotulo: "No va", clase: "gris" },
};

export const rotuloEstado = (e) => ESTADOS[e]?.rotulo || "Sin mirar";

/* Lo que está en juego: ni cerrada (pagada, rechazada, no va) ni afuera. */
const ABIERTOS = ["sin_mirar", "para_autorizar", "a_pagar", "vino_paga"];

export const FILTROS = [
  ["pendientes", "Pendientes"],
  ["sin_mirar", "Sin mirar"],
  ["para_autorizar", "Esperando autorización"],
  ["a_pagar", "A pagar"],
  ["vino_paga", "Vino paga"],
  ["pagada", "Pagadas"],
  ["rechazada", "Rechazadas"],
  ["no_va", "No van"],
  ["salieron", "Salieron"],
  ["todas", "Todas"],
];
export const ME_TOCA = ["me_toca", "Me toca"];

/* ¿Entra la fila en el filtro de estado? Las que salieron de «Facturas y
 * remitos» (dejaron de ser factura, las descartaron) aparecen SÓLO en
 * «Salieron» y en «Todas»: no cuentan en ningún otro lado. */
export function enFiltro(f, filtro, { yo = "" } = {}) {
  if (filtro === "todas") return true;
  if (filtro === "salieron") return !!f.fuera;
  if (f.fuera) return false;
  if (filtro === "pendientes") return ABIERTOS.includes(f.estado);
  if (filtro === "me_toca") {
    return (f.estado === "para_autorizar" && !!yo
            && String(f.pago?.pedida_a || "").toLowerCase() === String(yo).toLowerCase())
      || f.estado === "vino_paga";
  }
  return f.estado === filtro;
}

/* Cuántas hay en cada filtro (para el número de cada botón). */
export function cuantas(filas, ctx = {}) {
  const out = {};
  for (const [id] of [...FILTROS, ME_TOCA]) {
    out[id] = (filas || []).filter((f) => enFiltro(f, id, ctx)).length;
  }
  return out;
}

// ── Fechas y plata ──────────────────────────────────────────────────────────

const FMT_DIA = new Intl.DateTimeFormat("en-CA", {
  timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", hourCycle: "h23",
});

function partes(iso) {
  const t = Date.parse(iso || "");
  if (!Number.isFinite(t)) return null;
  const p = {};
  for (const { type, value } of FMT_DIA.formatToParts(t)) p[type] = value;
  return p;
}

/* El día argentino («2026-10-20»): lo que se compara con el filtro de fechas.
   A las 23:30 de acá ya es mañana en UTC. */
export function diaAR(iso) {
  const p = partes(iso);
  return p ? `${p.year}-${p.month}-${p.day}` : "";
}

/* «20/10», en Buenos Aires. */
export function diaCorto(iso) {
  const p = partes(iso);
  return p ? `${p.day}/${p.month}` : "";
}

/* «20/10 a las 14:32», en Buenos Aires. */
export function diaHora(iso) {
  const p = partes(iso);
  return p ? `${p.day}/${p.month} a las ${p.hour}:${p.minute}` : "";
}

/* «20/10 14:32», para el registro. */
export function diaHoraCorto(iso) {
  const p = partes(iso);
  return p ? `${p.day}/${p.month} ${p.hour}:${p.minute}` : "";
}

/* Una fecha sin hora («2026-09-07») se parte, no se parsea: como UTC caería el
   día anterior. */
export const fechaFactura = (f) => {
  const s = String(f || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [a, m, d] = s.split("-");
  return `${d}/${m}/${a}`;
};

const MONEDA = (m) => {
  const u = String(m || "").trim().toUpperCase();
  if (["USD", "U$S", "US$", "U$D"].includes(u)) return "US$";
  return "$";
};

/* «$ 1.234.567,89» / «US$ 10,00». `null` cuando no hay número: un «$ 0,00»
   inventado se lee como una factura de cero pesos. */
export function pesos(importe, moneda) {
  if (importe === null || importe === undefined || importe === "") return null;
  const n = Number(importe);
  if (!Number.isFinite(n)) return null;
  return `${MONEDA(moneda)} ${n.toLocaleString("es-AR", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

/* Lo que se escribe en «Importe autorizado», como se escribe acá: «100.000»
   son cien mil, «1.234.567,89» y «500000,5» también valen. Un
   <input type="number"> en Chromium con idioma es-AR leía «100.000» como 100
   y «1.234.567,89» como 1,23: es el único campo de plata de la pantalla, así
   que va como texto y se lee acá. Sin coma, un punto seguido de tres cifras
   es de miles («1.500» = 1500) y si no, es decimal («386955.85», lo que trae
   la base). NaN si no es un número. */
export function leerImporte(texto) {
  if (typeof texto === "number") return Number.isFinite(texto) ? texto : NaN;
  const t = String(texto ?? "").replace(/\s|\$/g, "");
  if (!t || !/^[0-9.,]+$/.test(t)) return NaN;
  let limpio;
  if (t.includes(",")) {
    // Una coma decimal y, si hay puntos, de miles ANTES de la coma.
    if ((t.match(/,/g) || []).length > 1 || t.lastIndexOf(".") > t.indexOf(",")) return NaN;
    limpio = t.replace(/\./g, "").replace(",", ".");
  } else if (/^\d{1,3}(\.\d{3})+$/.test(t)) {
    limpio = t.replace(/\./g, "");
  } else {
    if ((t.match(/\./g) || []).length > 1) return NaN;
    limpio = t;
  }
  const n = Number(limpio);
  return Number.isFinite(n) ? n : NaN;
}

/* El importe para precargar el campo: «386.955,85». */
export function importeParaEscribir(n) {
  if (n === null || n === undefined || n === "") return "";
  const x = Number(n);
  if (!Number.isFinite(x)) return "";
  return x.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export const cuitConGuiones = (c) => {
  const d = String(c || "").replace(/\D/g, "");
  return d.length === 11 ? `${d.slice(0, 2)}-${d.slice(2, 10)}-${d.slice(10)}` : (c || "");
};

const porDonde = (origen) => (origen === "mail" ? "por mail" : "por WhatsApp");

// ── Filtros de arriba ───────────────────────────────────────────────────────

/* ¿Llegó entre esas dos fechas (días argentinos, ambos inclusive)? */
export function enFechas(f, desde, hasta) {
  const d = diaAR(f.llego);
  if (desde && (!d || d < desde)) return false;
  if (hasta && (!d || d > hasta)) return false;
  return true;
}

/* Proveedores para elegir, POR CUIT y no por nombre: el mismo CUIT llega
   escrito de varias formas («D.E.R.» tiene tres). El nombre es el que más se
   repite. Las que no tienen CUIT van juntas, al final. */
export const SIN_CUIT = "sin-cuit";
export function proveedoresPorCuit(filas) {
  const por = new Map();
  for (const f of filas || []) {
    if (f.fuera) continue;
    const k = f.cuit11 || SIN_CUIT;
    const y = por.get(k) || { cuit: k, nombres: new Map(), cuantas: 0, totales: {} };
    const n = String(f.proveedor || "").trim();
    if (n) y.nombres.set(n, (y.nombres.get(n) || 0) + 1);
    y.cuantas += 1;
    /* Por moneda: sumar pesos con dólares es inventar un número. Las que no
       traen importe no suman (ni inventan un «$ 0,00»). */
    const imp = f.importe === null || f.importe === undefined || f.importe === "" ? NaN : Number(f.importe);
    if (Number.isFinite(imp)) {
      const m = MONEDA(f.moneda_norm || f.moneda) === "US$" ? "USD" : "ARS";
      y.totales[m] = (y.totales[m] || 0) + imp;
    }
    por.set(k, y);
  }
  return [...por.values()]
    .map((y) => {
      const nombre = [...y.nombres.entries()].sort((a, b) => b[1] - a[1])[0]?.[0]
        || (y.cuit === SIN_CUIT ? "Sin CUIT" : "Sin nombre");
      return { cuit: y.cuit, nombre, cuantas: y.cuantas, totales: y.totales };
    })
    .sort((a, b) => (a.cuit === SIN_CUIT) - (b.cuit === SIN_CUIT)
      || a.nombre.localeCompare(b.nombre, "es"));
}

export function rotuloProveedor(p) {
  if (p.cuit === SIN_CUIT) return `Sin CUIT · ${p.cuantas}`;
  const plata = Object.entries(p.totales || {})
    .sort(([a], [b]) => (a === "ARS" ? -1 : b === "ARS" ? 1 : a.localeCompare(b)))
    .map(([m, n]) => pesos(n, m)).join(" + ");
  return `${p.nombre} · ${cuitConGuiones(p.cuit)} · ${p.cuantas}${plata ? ` · ${plata}` : ""}`;
}

/* Lo que se busca, sin tildes ni mayúsculas, de los dos lados. */
export function coincide(f, texto) {
  const t = sinTildes(texto || "");
  if (!t) return true;
  return sinTildes([f.proveedor, f.numero, f.cuit, f.detalle, f.quien_nombre,
                    f.remitente_email].map((v) => String(v || "")).join(" ")).includes(t);
}

export const POR_LLEGADA = "llegada";
export const POR_FECHA = "fecha";
export const ORDENES = [
  [POR_LLEGADA, "Lo último que llegó"],
  [POR_FECHA, "Por fecha de la factura"],
];

export function ordenar(filas, modo = POR_LLEGADA) {
  const copia = [...(filas || [])];
  const ms = (x) => { const t = Date.parse(x || ""); return Number.isFinite(t) ? t : -Infinity; };
  if (modo === POR_FECHA) {
    return copia.sort((a, b) => String(b.fecha || b.llego || "").localeCompare(String(a.fecha || a.llego || "")));
  }
  return copia.sort((a, b) => ms(b.llego) - ms(a.llego));
}

// ── Las de antes del arranque ──────────────────────────────────────────────

/* Nico (20/10, por mail): «las 86 facturas las vi y las leí a modo de prueba.
   No siguieron ningún circuito de pago ni autorización.» La bandeja arranca
   con las que llegan desde el arranque: las que llegaron antes y nadie movió
   nunca no se muestran (ni en la lista, ni en los números de los filtros, ni
   en el total por proveedor), salvo que se pida. «Nadie la movió» es que no
   tiene fila de pago (`pago` vacío): una que alguien devolvió a la bandeja
   («Volver a la bandeja» desde «No va») también está «Sin mirar», pero ésa
   sí siguió un circuito y se ve. Lo que no cambia: la base, los permisos,
   «Facturas y remitos», y que `?ver=` (y «Ver la otra») la muestre igual. */
export const viejaSinMirar = (f) =>
  !!f?.antes_del_arranque && f.estado === "sin_mirar" && !f.pago?.estado;

/* Las filas de la bandeja: sin las viejas que nadie movió, salvo que se pidan. */
export function sinViejas(filas, ver = false) {
  const l = filas || [];
  return ver ? l : l.filter((f) => !viejaSinMirar(f));
}

const diaDelArranque = (arranque) => (arranque ? fechaFactura(arranque)?.slice(0, 5) : null) || "arranque";

// ── El resumen de arriba ────────────────────────────────────────────────────

/* Cuántas hay en cada estado (sin las que salieron) y cuánto hay «A pagar»,
   por moneda: sumar pesos con dólares es inventar un número. Las viejas que
   nadie movió se cuentan aparte (`viejasSinMirar`) y, mientras no se vean,
   no suman en «sin mirar». */
export function resumen(filas, arranque = null, { verViejas = false } = {}) {
  const r = { sin_mirar: 0, para_autorizar: 0, a_pagar: 0, vino_paga: 0, aPagarPorMoneda: {},
              viejasSinMirar: 0 };
  for (const f of filas || []) {
    if (viejaSinMirar(f)) {
      r.viejasSinMirar += 1;
      if (!verViejas) continue;
    }
    if (f.fuera) continue;
    if (f.estado in r) r[f.estado] += 1;
    if (f.estado === "a_pagar") {
      const m = f.pago?.moneda_autorizada || "ARS";
      r.aPagarPorMoneda[m] = (r.aPagarPorMoneda[m] || 0) + (Number(f.pago?.importe_autorizado) || 0);
    }
  }
  r.arranque = arranque;
  r.verViejas = verViejas;
  return r;
}

export function textoResumen(r) {
  const plata = Object.entries(r.aPagarPorMoneda)
    .map(([m, n]) => pesos(n, m)).join(" + ");
  const partes = [
    `${r.sin_mirar} sin mirar`,
    `${r.para_autorizar} esperando autorización`,
    `${r.a_pagar} a pagar${r.a_pagar && plata ? ` (${plata})` : ""}`,
    `${r.vino_paga} vino paga`,
  ];
  return partes.join(" · ");
}

/* «89 llegaron antes del 20/10 y no siguieron ningún circuito: no se
   muestran.» Al lado va el botón (`rotuloVerViejas`). Con `?ver=` sobre una
   de ésas (`mirandoUna`), aclara que la que está a la vista es una: si no,
   la línea dice «no se muestran» con una a la vista. */
export function textoViejas(r, { mirandoUna = false } = {}) {
  const n = r.viejasSinMirar;
  if (!n) return null;
  const dia = diaDelArranque(r.arranque);
  const uno = n === 1;
  const aclaracion = !mirandoUna ? ""
    : uno ? " (es la que estás mirando)" : " (la que estás mirando es una de ellas)";
  const queSePasa = r.verViejas
    ? (uno ? "se muestra igual" : "se muestran igual")
    : (uno ? "no se muestra" : "no se muestran") + aclaracion;
  return `${n} ${uno ? "llegó" : "llegaron"} antes del ${dia} y `
    + `${uno ? "no siguió" : "no siguieron"} ningún circuito: ${queSePasa}.`;
}

export function rotuloVerViejas(r) {
  if (!r.verViejas) return "Ver igual";
  return r.viejasSinMirar === 1 ? "Ocultarla" : "Ocultarlas";
}

/* Cuando no queda ninguna para mostrar porque todas son de antes. */
export function textoSinNuevas(r) {
  return `Todavía no llegó ninguna factura desde el ${diaDelArranque(r.arranque)}`;
}

// ── Lo que dice cada carta ─────────────────────────────────────────────────

/* La línea de estado: quién hizo qué y cuándo. */
export function lineaEstado(f) {
  const p = f.pago || {};
  const nota = (t) => (t ? ` · “${t}”` : "");
  switch (f.estado) {
    case "para_autorizar":
      return `Esperando autorización de ${p.pedida_a_nombre || "quien autoriza"}`
        + `${p.pedida_en ? ` desde el ${diaCorto(p.pedida_en)}` : ""}${nota(p.pedida_nota)}`;
    case "a_pagar":
      return `Autorizó ${p.autorizada_por_nombre || "—"} el ${diaCorto(p.autorizada_en)}: `
        + `${pesos(p.importe_autorizado, p.moneda_autorizada)} por `
        + `${String(f.forma_pago_etiqueta || p.forma_pago || "").toLowerCase()}${nota(p.autorizada_nota)}`;
    case "vino_paga":
      return `Vino paga, según ${p.vino_paga_por_nombre || "—"} el ${diaCorto(p.vino_paga_en)}: `
        + `“${p.vino_paga_motivo || ""}”. Falta que la ratifique quien autoriza.`;
    case "pagada":
      return p.pagada_como === "vino_paga"
        ? `Vino paga · ratificó ${p.ratificada_por_nombre || "—"} el ${diaCorto(p.ratificada_en || p.pagada_en)}`
        : `Pagada el ${diaCorto(p.pagada_en)} (la marcó ${p.pagada_por_nombre || "—"})`;
    case "rechazada":
      return `Rechazada por ${p.paso_por_nombre || "—"} el ${diaCorto(p.paso_en)}: “${p.motivo || ""}”`;
    case "no_va":
      return `No va, según ${p.paso_por_nombre || "—"} el ${diaCorto(p.paso_en)}: “${p.motivo || ""}”`;
    default:
      return null;
  }
}

export function lineaMismaPersona(f) {
  const p = f.pago || {};
  if (f.estado !== "pagada" || !p.misma_persona) return null;
  return p.pagada_como === "vino_paga"
    ? "Se la ratificó él mismo"
    : "La autorizó y la marcó pagada la misma persona";
}

/* Las otras copias que se pueden ver y que traban (no las rechazadas). */
const copiasQueTraban = (f) =>
  (f.grupo || []).filter((g) => g.visible && (g.marca === "viva" || g.marca === "foto"));

/* «Es la misma factura que la que mandó Nicolas Cantarini por mail el 14/09 a
   las 14:39». Nunca un id: el que lee no sabe qué es «mail#2». */
export function quienYCuando(g) {
  const quien = g.quien_nombre ? `la que mandó ${g.quien_nombre}` : "la que llegó";
  return `${quien} ${porDonde(g.origen)} el ${diaHora(g.llego)}`;
}

/* Los avisos, en el orden en que importan. `otra` es la key de la copia a la
   que lleva «Ver la otra». */
export function alertas(f) {
  const out = [];
  const vistas = new Set();
  /* En una cerrada (pagada, rechazada, «No va») o que salió, la copia no
     traba nada que se pueda hacer acá: se dice que hay otra, en gris, sin el
     «hasta que una quede en No va». */
  const abierta = ABIERTOS.includes(f.estado) && !f.fuera;
  for (const g of copiasQueTraban(f)) {
    const k = `${g.regla}-${g.key}`;
    if (vistas.has(k)) continue;
    vistas.add(k);
    if (!abierta) {
      if (vistas.has(`otra-${g.key}`)) continue;
      vistas.add(`otra-${g.key}`);
      out.push({ id: k, tono: "gris", otra: g.key,
        texto: `Hay otra copia: ${quienYCuando(g)} (${rotuloEstado(g.estado)}).` });
      continue;
    }
    if (g.regla === "R1") {
      out.push({ id: k, tono: "mal", otra: g.key,
        texto: `Es la misma factura que ${quienYCuando(g)}. Hasta que una quede en «No va», `
             + "ninguna se autoriza ni se paga." });
    } else if (g.regla === "R3") {
      out.push({ id: k, tono: "mal", otra: g.key,
        texto: `Es el mismo archivo que llegó ${porDonde(g.origen)} el ${diaCorto(g.llego)} `
             + `(${[g.proveedor, g.numero].filter(Boolean).join(" ") || "sin leer"}`
             + `${g.quien_nombre ? `, lo mandó ${g.quien_nombre}` : ""}).` });
    } else if (g.regla === "R2") {
      out.push({ id: k, tono: "mal", otra: g.key,
        texto: `Mismo número e importe que la de ${g.proveedor || "otro proveedor"}`
             + `${g.cuit ? ` (CUIT ${cuitConGuiones(g.cuit)})` : " (sin CUIT)"}. `
             + "Si es la misma, una va a «No va»." });
    }
  }
  if (abierta && (f.grupo || []).some((g) => !g.visible && (g.marca === "viva" || g.marca === "foto"))) {
    out.push({ id: "oculta", tono: "mal",
      texto: "Hay otra copia que no podés ver: avisale a quien autoriza." });
  }
  if (f.pagada_dos_veces) {
    const o = (f.grupo || []).find((g) => g.visible && g.estado === "pagada");
    out.push({ id: "dos-veces", tono: "mal", otra: o?.key,
      texto: `Se marcó pagada dos veces${o ? `: la otra la marcó ${o.pagada_por_nombre || "—"} `
             + `el ${diaCorto(o.pagada_en)}` : ""}.` });
  }
  const conPlata = f.estado === "pagada" || f.estado === "vino_paga";
  if (f.fuera && conPlata) {
    /* Pagada o «vino paga»: aunque la sacaron, sigue trabando a sus copias,
       y desde acá no se mueve. El camino para moverla es devolverla. */
    out.push({ id: "fuera", tono: "mal",
      texto: `La sacaron de las facturas en Facturas y remitos, pero ${f.estado === "pagada" ? "está pagada" : "dijeron que vino paga"}: `
           + "sigue trabando a sus copias. Para moverla acá, que la devuelvan al cajón allá." });
  } else if (conPlata && f.pago?.cambio_despues_en) {
    out.push({ id: "cambio-despues", tono: "mal",
      texto: /^Le cambiaron|^La sacaron/.test(f.pago?.motivo || "")
        ? `${f.pago.motivo.replace(/\.$/, "")}: revisalo.`
        : "Le cambiaron la factura después de marcarla pagada: revisalo." });
  } else if (f.fuera) {
    out.push({ id: "fuera", tono: "gris", texto: "La sacaron de las facturas en Facturas y remitos." });
  }
  if (f.sin_clave && !f.clave_manual) {
    out.push({ id: "sin-clave", tono: "aviso",
      texto: "No tiene CUIT o número: para autorizarla hay que escribirlos del papel." });
  }
  if (f.cuit_dudoso) {
    out.push({ id: "cuit-dudoso", tono: "aviso",
      texto: `El CUIT ${f.cuit} no da: puede estar mal leído.` });
  }
  if (f.cuit_nuestro) {
    out.push({ id: "cuit-nuestro", tono: "aviso",
      texto: `Ese CUIT es de ${f.cuit_nuestro}: se leyó el de quien la recibe.` });
  }
  if (f.sin_importe && ABIERTOS.includes(f.estado)) {
    out.push({ id: "sin-importe", tono: "aviso",
      texto: "No se leyó el importe: escribilo al autorizar." });
  }
  if ((f.parecidas || []).length) {
    const todas = [f, ...f.parecidas];
    const numeros = todas.map((x) => String(x.numero || "").split("-").pop().replace(/^0+/, ""))
      .sort((a, b) => Number(a) - Number(b));
    out.push({ id: "parecidas", tono: "aviso",
      texto: `${f.proveedor || "El proveedor"} facturó ${todas.length} iguales el `
           + `${fechaFactura(f.fecha)?.slice(0, 5) || "mismo día"} (${numeros.join(", ")} · `
           + `${pesos(f.importe, f.moneda)}${f.detalle ? ` · ${f.detalle}` : ""}). `
           + "Si no son papeles distintos, puede ser un cobro doble." });
  }
  if (f.antes_del_arranque && ["sin_mirar", "para_autorizar"].includes(f.estado)) {
    out.push({ id: "arranque", tono: "info",
      texto: "Llegó antes de que existiera esta bandeja: fijate en el sistema de siempre si ya se pagó." });
  }
  if (f.vino_paga_antes) {
    out.push({ id: "vino-paga-antes", tono: "info",
      texto: `Según ${f.vino_paga_antes.por_nombre || "alguien"} vino paga: “${f.vino_paga_antes.motivo || ""}”.` });
  }
  if (f.cuit_nuevo && ABIERTOS.includes(f.estado)) {
    out.push({ id: "cuit-nuevo", tono: "info",
      texto: "Primera factura de este CUIT y no está en los padrones: mirá que el proveedor sea real." });
  }
  return out;
}

/* «Corregida por Andrés el 20/10: importe $ 1.000,00 → $ 100,00». */
const ROTULO_CAMPO = {
  importe: "importe", cuit: "CUIT", numero: "número", moneda: "moneda", tipo: "tipo",
  archivo: "papel", ruta: "papel", mime: "papel", anulado_en: "descarte", asunto: "asunto",
};
export function textoCambio(detalle, moneda) {
  return (Array.isArray(detalle) ? detalle : [])
    .map((d) => {
      const r = ROTULO_CAMPO[d.campo] || d.campo;
      if (d.campo === "importe") {
        return `${r} ${pesos(d.antes, moneda) || "—"} → ${pesos(d.despues, moneda) || "—"}`;
      }
      if (["archivo", "ruta", "mime"].includes(d.campo)) return "cambió el papel";
      if (d.campo === "anulado_en") return d.despues ? "la descartaron" : "la devolvieron al cajón";
      return `${r} ${d.antes ?? "—"} → ${d.despues ?? "—"}`;
    })
    .filter((x, i, a) => a.indexOf(x) === i)
    .join(", ");
}

export function lineasCorregida(f) {
  return (f.corregida || []).map((c) =>
    `Corregida por ${c.por_nombre || "—"} el ${diaCorto(c.en)}: ${textoCambio(c.detalle, f.moneda)}`);
}

// ── Lo que hay que confirmar para autorizar ────────────────────────────────

/* El texto de cada casilla. La lista de cuáles la manda la base
   (`revisiones_que_pide`); acá sólo se dicen. */
export function textoRevision(clave, f, { importe, cuitNuestro } = {}) {
  const rech = (f.grupo || []).find((g) => g.visible && g.marca === "rechazada");
  switch (clave) {
    case "antes_del_arranque": return "Me fijé en el sistema de siempre: no está paga.";
    case "cuit_dudoso": return "Miré el papel: el proveedor es el que dice, aunque el CUIT esté mal leído.";
    case "cuit_nuestro":
      return `Miré el papel: ese CUIT es de ${cuitNuestro || f.cuit_nuestro || "una empresa nuestra"}, el que la recibe; `
        + "el proveedor es el que dice.";
    case "corregida": return "Vi lo que le corrigieron.";
    case "vino_paga_antes":
      return `${f.vino_paga_antes?.por_nombre || "Alguien"} dijo que vino paga`
        + `${f.vino_paga_antes?.motivo ? ` («${f.vino_paga_antes.motivo}»)` : ""}. Me fijé: no está paga.`;
    case "otra_rechazada":
      return `La otra copia la rechazó ${rech?.por_nombre || "quien autoriza"}`
        + `${rech?.motivo ? ` («${rech.motivo}»)` : ""}. Igual se paga.`;
    case "parecida": return "Miré los papeles: no es un cobro doble.";
    case "cuit_nuevo": return "Miré que el proveedor es real.";
    case "importe_distinto":
      return f.importe === null || f.importe === undefined
        ? `La factura no trae importe: voy a autorizar ${pesos(leerImporte(importe), f.moneda_norm || "ARS") || "—"}.`
        : `La factura dice ${pesos(f.importe, f.moneda)}; voy a autorizar ${pesos(leerImporte(importe), f.moneda) || "—"}.`;
    case "importe_mayor": return "Es MÁS que la factura.";
    case "cambio_desde_vino_paga": return "Vi lo que cambió desde que vino paga.";
    default: return clave;
  }
}

/* Lo que la base va a pedir para autorizar ESE importe: lo de la factura más,
   si el importe es otro, «importe distinto» (y «mayor» si es más). `base` es
   la lista que manda la base para lo que se tipeó (CUIT y número de una sin
   clave, `factura_pago_que_pide`); sin eso, la de la bandeja. */
export function revisionesParaAutorizar(f, importe, base = null) {
  const r = [...(Array.isArray(base) ? base : (f.revisiones_que_pide || []))];
  const n = leerImporte(importe);
  if (importe !== "" && importe !== null && importe !== undefined && Number.isFinite(n)) {
    const fac = f.importe === null || f.importe === undefined ? null : Number(f.importe);
    if (fac === null || Math.round(n * 100) !== Math.round(fac * 100)) r.push("importe_distinto");
    if (fac !== null && n > fac) r.push("importe_mayor");
  }
  return r;
}

export function diferencia(f, importe) {
  const n = leerImporte(importe);
  if (f.importe === null || f.importe === undefined || !Number.isFinite(n)) return null;
  const fac = Number(f.importe);
  if (Math.round(n * 100) === Math.round(fac * 100)) return null;
  return `La factura dice ${pesos(fac, f.moneda)}; vas a autorizar ${pesos(n, f.moneda)} `
    + `(diferencia ${pesos(Math.abs(n - fac), f.moneda)}).`;
}

// ── Los botones ─────────────────────────────────────────────────────────────

/* Qué botones ve quien mira, según el estado y sus llaves. Los que frena una
   repetida aparecen deshabilitados, con el porqué (`traba`). Quien autoriza
   ve «Autorizar» primero. `unicoQueAutoriza`: si el único que autoriza es
   quien mira, «Pedir autorización a…» no tiene a quién; `null` es «todavía
   no se sabe» (la lista está viniendo): tampoco se muestra, así no aparece y
   desaparece. */
export function botones(f, { cargar = false, autorizar = false, unicoQueAutoriza = false } = {}) {
  const b = [];
  if (f.fuera) return b;
  const traba = f.bloqueada ? (f.bloqueo || "Hay otra copia de esta factura.") : null;
  const e = f.estado;
  if (e === "sin_mirar" || e === "para_autorizar") {
    if (autorizar) {
      b.push({ id: "autorizar", rotulo: "Autorizar", traba });
      b.push({ id: "rechazar", rotulo: "Rechazar", title: "Es nuestra, pero no se paga" });
    }
    if (cargar) {
      b.push({ id: "vino_paga", rotulo: "Ya vino paga", traba });
      if (unicoQueAutoriza === false) {
        b.push({ id: "pedir", rotulo: e === "para_autorizar" ? "Pedírsela a otro" : "Pedir autorización a…" });
      }
      b.push({ id: "no_va", rotulo: "No va",
               title: "No es una factura a pagar: repetida, no es nuestra, varias en un papel" });
    }
  } else if (e === "a_pagar") {
    if (cargar) b.push({ id: "pagada", rotulo: "Marcar pagada", traba });
    if (autorizar) b.push({ id: "rechazar", rotulo: "Rechazar", title: "Es nuestra, pero no se paga" });
  } else if (e === "vino_paga") {
    if (autorizar) {
      b.push({ id: "ratificar", rotulo: "Ratificar", traba });
      b.push({ id: "no_ratificar", rotulo: "No la ratifico" });
    }
  } else if (e === "pagada") {
    if (autorizar) b.push({ id: "deshacer_pagada", rotulo: "Deshacer: no estaba pagada" });
  } else if (e === "rechazada") {
    if (autorizar) b.push({ id: "reabrir", rotulo: "Volver a la bandeja" });
  } else if (e === "no_va") {
    if (cargar || autorizar) b.push({ id: "reabrir", rotulo: "Volver a la bandeja" });
  }

  const copias = copiasQueTraban(f);
  if (copias.length) {
    const puedeRepetida = ((e === "sin_mirar" || e === "para_autorizar") && (cargar || autorizar))
      || ((e === "a_pagar" || e === "vino_paga") && autorizar);
    if (puedeRepetida) b.push({ id: "es_repetida", rotulo: "Ésta es la repetida" });
    const r2 = copias.filter((g) => g.regla === "R2" && g.cuit_ok);
    if (autorizar && f.cuit_ok && r2.length && ABIERTOS.includes(e)) {
      b.push({ id: "son_distintas", rotulo: "Son de proveedores distintos" });
    }
  }
  return b;
}

/* Las copias que se pueden elegir en «Ésta es la repetida» / «Son distintas». */
export function copiasPara(f, accion) {
  const c = copiasQueTraban(f);
  const lista = accion === "son_distintas" ? c.filter((g) => g.regla === "R2" && g.cuit_ok) : c;
  const vistas = new Set();
  return lista.filter((g) => (vistas.has(g.key) ? false : vistas.add(g.key)));
}

export function motivoRepetida(g) {
  return `Repetida de ${quienYCuando(g)}.`;
}

export const ATAJOS_NO_VA = [
  "No es para nosotros",
  "Es una nota de crédito: descontala en el sistema",
  "Son varias facturas en un papel: que entren de a una",
];
export const YA_ESTABA_PAGA = "Ya estaba pagada antes de la bandeja";

// ── El registro ─────────────────────────────────────────────────────────────

export function textoPaso(p, { formas = [], nombres = {} } = {}) {
  const forma = (formas.find((x) => x.clave === p.forma_pago)?.etiqueta || p.forma_pago || "")
    .toLowerCase();
  const m = (t) => (t ? `: “${t}”` : "");
  switch (p.accion) {
    case "pedir": return `Pidió autorización a ${nombres[p.pedida_a] || p.pedida_a || "—"}${m(p.nota)}`;
    case "autorizar": return `Autorizó ${pesos(p.importe_autorizado, p.moneda)} por ${forma}${m(p.nota)}`;
    case "vino_paga": return `Dijo que vino paga${m(p.motivo)}`;
    case "ratificar": return "Ratificó que vino paga";
    case "no_ratificar": return `No la ratificó${m(p.motivo)}`;
    case "pagada": return `La marcó pagada${m(p.nota)}`;
    case "rechazar": return `La rechazó${m(p.motivo)}`;
    case "no_va": return `Dijo que no va${m(p.motivo)}`;
    case "es_repetida": return `La marcó repetida${m(p.motivo)}`;
    case "son_distintas": return `Dijo que son de proveedores distintos${m(p.motivo)}`;
    case "deshacer_pagada": return `Deshizo la pagada${m(p.motivo)}`;
    case "reabrir": return `La volvió a la bandeja${m(p.motivo)}`;
    case "salio": return "La sacaron de las facturas en Facturas y remitos";
    case "cambio_factura": {
      const t = textoCambio(p.detalle);
      const vuelta = p.estado_antes !== p.estado_despues
        ? ` · pasó a ${rotuloEstado(p.estado_despues)}` : "";
      return `Cambió la factura${t ? `: ${t}` : ""}${vuelta}`;
    }
    default: return p.accion || "—";
  }
}

export function lineaPaso(p, ctx) {
  return `${diaHoraCorto(p.hecho_en)} · ${p.hecho_por_nombre || "—"} · ${textoPaso(p, ctx)}`;
}

// ── El chip de «Facturas y remitos» ────────────────────────────────────────

/* Lo que ve en «Facturas y remitos» quien mandó la factura, sin la llave de
   Proveedores. Nunca la forma de pago, el importe ni la nota. */
export function chipDe(c) {
  if (!c || !c.estado || c.estado === "sin_mirar") return null;
  const dia = diaCorto(c.cuando);
  const extra = !c.misma_persona ? ""
    : c.pagada_como === "vino_paga" ? " (se la ratificó él mismo)"
      : " (la autorizó y la marcó pagada la misma persona)";
  const title = `En Proveedores › Facturas a pagar${extra}`;
  switch (c.estado) {
    case "para_autorizar": return { texto: "Esperando autorización", clase: "info", title };
    case "a_pagar": return { texto: `Autorizó ${c.quien_nombre || "—"} ${dia}`, clase: "ok", title };
    case "vino_paga": return { texto: "Vino paga", clase: "info", title };
    case "pagada": return { texto: `Pagada ${dia}`, clase: "ok", title };
    case "rechazada":
      return { texto: `Rechazada ${dia}`, clase: "mal", title, linea: `Rechazada: “${c.motivo || ""}”` };
    case "no_va":
      return { texto: "No va", clase: "gris", title, linea: `No va: “${c.motivo || ""}”` };
    default: return null;
  }
}

/* Lo que se le avisa en «Facturas y remitos» a quien puede corregir o
   descartar una factura que está en Proveedores: qué le pasa allá si la toca.
   Descartarla también cuenta (antes sólo se avisaba de corregir). */
export function avisoAlCorregir(estado) {
  switch (estado) {
    case "para_autorizar":
      return "Está esperando autorización en Proveedores: si la descartás, sale de Facturas a pagar.";
    case "a_pagar":
      return "Está autorizada: si le cambiás el importe, el CUIT, el número o el papel, vuelve a pedir "
        + "autorización; si la descartás, sale de Facturas a pagar y pierde la autorización.";
    case "vino_paga":
      return "Dijeron que vino paga: si la corregís o la descartás, queda anotado y sigue trabando a "
        + "sus copias en Facturas a pagar.";
    case "pagada":
      return "Está pagada: si la corregís o la descartás, queda anotado que cambió después del pago.";
    default:
      return null;
  }
}

// ── Las facturas fijas ─────────────────────────────────────────────────────

/* Daiana (20/10): «Facturas fijas: Alquileres, Servicios, Seguros, Abonos,
   proveedores de mercadería, combustible». La idea de Nico es que en ésas
   alguien se fije que no hayan subido de valor ni sea un valor exagerado.

   Lo calcula la base (`facturas_fijas()`, 20261022155856): qué proveedor es
   fijo (por CUIT), y de cada factura suya la anterior del mismo proveedor y
   cuánto subió. Acá sólo se dice. `fijas` es lo que contestó la base, o
   null si no contestó (la migración no está, la red): entonces no se dice
   nada y la bandeja se ve como antes. */

/* Lo de ESA factura (por key), o null si no es de un proveedor fijo. */
export function fijaDe(fijas, f) {
  if (!fijas || !f) return null;
  return (fijas.facturas || []).find((x) => x.key === f.key) || null;
}

/* El proveedor fijo de ese CUIT (para «Cambiar»), o null. */
export function proveedorFijo(fijas, cuit11) {
  if (!fijas || !cuit11) return null;
  return (fijas.fijos || []).find((x) => x.cuit11 === cuit11) || null;
}

/* «+18,5 %» / «−4 %»: con signo siempre, para que una baja no se lea como
   suba. */
export function pctTexto(p) {
  const n = Number(p);
  if (p === null || p === undefined || p === "" || !Number.isFinite(n)) return null;
  const abs = Math.abs(n).toLocaleString("es-AR", { maximumFractionDigits: 1 });
  return `${n > 0 ? "+" : n < 0 ? "−" : ""}${abs} %`;
}

/* La línea de una factura fija, con su tono: «mal» si subió más que el
   umbral (la marca fuerte), «aviso» si subió menos, «info» si bajó o quedó
   igual, «gris» sin con qué comparar. */
export function lineaFija(x, f, umbral = null) {
  if (!x) return null;
  const moneda = f?.moneda_norm || f?.moneda;
  const que = `Fija (${x.etiqueta || x.categoria})`;
  const a = x.anterior;
  if (!a) return { tono: "gris", texto: `${que}: no hay una anterior para comparar.` };
  const laAnterior = `la anterior (${[a.numero, a.fecha ? `del ${fechaFactura(a.fecha)}` : null]
    .filter(Boolean).join(" ") || "sin número"}, ${pesos(a.importe, moneda)})`;
  const dif = Number(x.diferencia);
  if (x.diferencia === null || x.diferencia === undefined || !Number.isFinite(dif)) {
    return { tono: "aviso", texto: `${que}: no se leyó el importe para compararla con ${laAnterior}.` };
  }
  if (Math.round(dif * 100) === 0) return { tono: "info", texto: `${que}: igual que ${laAnterior}.` };
  const pct = pctTexto(x.pct);
  if (dif > 0) {
    const tope = pctTexto(umbral);
    const pasa = x.sube_mucho && tope ? `, más del ${tope.replace(/^\+/, "")}` : "";
    return {
      tono: x.sube_mucho ? "mal" : "aviso",
      texto: `${que}: subió ${pesos(dif, moneda)} (${pct}${pasa}) contra ${laAnterior}.`,
    };
  }
  return { tono: "info", texto: `${que}: bajó ${pesos(-dif, moneda)} (${pct}) contra ${laAnterior}.` };
}

/* ¿Puede marcar a ese proveedor como fijo? Con alguna de las dos llaves y un
   CUIT que dé y no sea nuestro (la base dice lo mismo). Sin respuesta de la
   base (`fijas` null), no se ofrece: no habría dónde guardarlo. */
export const puedeMarcarFija = (f, fijas, { cargar = false, autorizar = false } = {}) =>
  !!fijas && (cargar || autorizar) && !!f?.cuit_ok && !!f?.cuit11 && !f.fuera;

// ── «Pedir autorización a…»: el sector de cada uno ─────────────────────────

/* Daiana (20/10): Taller de Camiones → Andrés Millet u Oscar Rincón; Taller
   de Furgones → Federico; Tráfico → Gabriel Boveda; Administración → ella; y
   en cualquier sector, Nico y ella.

   Cruza quiénes autorizan (la base, por la llave) con su sector
   (`proveedores_autorizantes`) y los ordena por sector: primero el orden del
   primer sector de cada uno, después el nombre. Sólo orienta: no saca a
   nadie de la lista. Quien autoriza y no tiene sector va al final, sin
   rótulo; una fila de sector de alguien que no autoriza no agrega a nadie. */
export function conSector(autorizan, sectores) {
  const por = new Map();
  for (const s of sectores || []) {
    const m = String(s.mail || "").toLowerCase();
    if (!m || !s.sector) continue;
    const l = por.get(m) || [];
    l.push({ sector: s.sector, orden: Number.isFinite(Number(s.orden)) ? Number(s.orden) : 0 });
    por.set(m, l);
  }
  return (autorizan || [])
    .map((a) => {
      const l = (por.get(String(a.mail || "").toLowerCase()) || []).sort((x, y) => x.orden - y.orden);
      return { ...a, sectores: [...new Set(l.map((x) => x.sector))], orden: l.length ? l[0].orden : Infinity };
    })
    .sort((a, b) => a.orden - b.orden || String(a.nombre || "").localeCompare(String(b.nombre || ""), "es"));
}

/* «Andrés Millet · Taller de Camiones», «Daiana Cantarini · Administración ·
   Cualquier sector». Sin sector, el nombre solo. */
export const rotuloAutoriza = (a) =>
  [a.nombre || a.mail, ...(a.sectores || [])].join(" · ");

/* ¿La traba una copia, AHORA? Sólo en las abiertas: en una cerrada (pagada,
   rechazada, «No va») o que salió, no hay nada que trabar acá. */
export const trabada = (f) => !!f.bloqueada && ABIERTOS.includes(f.estado) && !f.fuera;

/* «Llegó hace…» reusa el de Facturas y remitos: mismo reloj, mismas palabras. */
export { cuandoLlego };
